import { WarehouseType } from '../warehouse/warehouse.type';
import { OrganizationType } from '../organization/organization.type';
import { ShippingPackageType } from './shipping-package.types';
import { AcsAtaVirtualOverrideType, CommentType } from '../order/order.type';
import { SimState } from '../tc-sim/tc-sim.type';
import { CURRENT_STATUS } from '../enums/current-status.enum';
import { UserType } from '../user/user.type';

export enum FulfillmentOrderStatus {
  NewOrder = 'New Order',
  PendingPayment = 'Pending Payment',
  ApprovedToFulfill = 'Approved to Fulfill',
  AcceptedToFulfill = 'Accepted to Fulfill',
  PendingFulfillment = 'Pending Fulfillment',
  ReadyToShip = 'Ready to Ship',
  PartiallyShipped = 'Partially Shipped',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
  Cancelled = 'Cancelled',
}

export enum ShippingPriorities {
  Standard = 'Standard',
}

export type ModelType = {
  id: number;
  name: string;
  brand: BrandType;
  isAta?: boolean;
  isBattery?: boolean;
  isDialer?: boolean;
  isEnclosure?: boolean;
  isPowerCycle?: boolean;
  isRouter?: boolean;
  isSim?: boolean;
  isSmartphone?: boolean;
  isGlobal?: boolean;
  isNonSerialized?: boolean;
  params?: {
    ports?: number;
    channels?: number;
  };
  availableInventory?: number;
};

export type BrandType = {
  id: number;
  name: string;
};

export type BrandWithModelsType = {
  id: number;
  name: string;
  models: ModelType[];
};

export type InventoryListItem = {
  model: ModelType;
  pendingIn: number;
  pendingOut: number;
  available: number;
};

export type DeviceType = {
  id: number;
  hash: string;
  displayName: string;
  description: string;
  serialNumber: string;
  model: ModelType;
  warehouse: WarehouseType;
  createdAt: string;
  modifiedAt: string;
  availableForSale?: boolean;
  currentStatus: CURRENT_STATUS;
  deviceStatus: DeviceStatus;

  owner?: OrganizationType;
  atas?: AtaDeviceType[];
  dialers?: DialerDeviceType[];
  routers?: RouterDeviceType[];
  sims?: SimDeviceType[];
  smartphones?: SmartphoneDeviceType[];
  powerCycles?: PowerCycleDeviceType[];
  shippingPackage?: ShippingPackageType;
  qaCheckBy?: UserType;
  qaNote?: string | null;
};

export type AtaDeviceType = {
  id: number;
  hash: string;
  createdAt: string;
  modifiedAt: string;
  mac: string;
  deviceKey?: string | null;
  adminPassword?: string | null;
  firmwareId?: string | null;
  acsAtaDevice?: AcsAtaDeviceType;
};

export type AcsAtaDeviceType = {
  id: number;
  hash: string;
  createdAt: string;
  modifiedAt: string;
  firmware: {
    id: number;
    version: string;
  } | null;
  mcuFirmware: {
    id: number;
    version: string;
  } | null;
  firmwareVersion?: string | null;
  acsAdminPassword?: string;
  status?: number;
  statusUpdateDate?: string | null;
  deviceKey?: string | null;
  factoryPassword?: string | null;
  tcUserPassword?: string | null;
  nextCheckInDate?: string | null;
  imei?: string | null;
  configOverrides: null | Array<AcsAtaVirtualOverrideType>;
  ports?: Array<{
    fxsPort: number;
    portConfigOverrides: null | Array<AcsAtaVirtualOverrideType>;
  }>;
};

export type DialerDeviceType = {
  id: number;
  hash: string;
  displayName: string;
  createdAt: string;
  modifiedAt: string;
  csAccount: string;
  dialerType: null | { id: number; displayName: string };
  csPrimaryReceiver: { number: string; countryCode: string } | null;
  csSecondaryReceiver: { number: string; countryCode: string } | null;
};

export type RouterDeviceType = {
  id: number;
  hash: string;
  displayName: string;
  createdAt: string;
  modifiedAt: string;
  imei: string;
};

export type SmartphoneDeviceType = {
  id: number;
  hash: string;
  displayName: string;
  createdAt: string;
  modifiedAt: string;
  imei: string | null;
  imei2: string | null;
};

export type SimDeviceType = {
  id: number;
  hash: string;
  displayName: string;
  createdAt: string;
  modifiedAt: string;
  iccid: string;
  eid: string;
  externalSystemInstanceId?: number;
  isConnected?: boolean;
  status?: SimState;
  mobile: { number: string; countryCode: string } | null;
};

export type PowerCycleDeviceType = {
  id: number;
  hash: string;
  displayName: string;
  createdAt: string;
  modifiedAt: string;
  password: string;
};

export type FulfillmentOrderItemType = {
  id: number;
  quantity: number;
  model: ModelType;
  isNonSerialized?: boolean;
};

export type FulfillmentOrderType = {
  id: number;
  hash: string;
  displayName: string;
  createdAt: string;
  modifiedAt: string;
  shippingDetails: InventoryShippingType | null;
  devices: Array<DeviceType>;
  fromWarehouse: WarehouseType;
  toWarehouse: WarehouseType;
  status: FulfillmentOrderStatus;
  items: Array<FulfillmentOrderItemType>;
  accessoryBundles?: Array<{
    id: number;
    quantity: number;
    bundle: AccessoryBundleType;
  }>;
  approvalDate: string | null;
  comments: Array<CommentType>;
  approvedBy: {
    email: string;
    id: number;
    firstname: string;
    lastname: string;
  } | null;
  invoiceChecker: {
    email: string;
    id: number;
    firstname: string;
    lastname: string;
  } | null;
  invoiceNumber?: string;
  invoiceCreatedDate: string | null;
  paymentChecker: {
    email: string;
    id: number;
    firstname: string;
    lastname: string;
  } | null;
  paymentNumber?: string;
  paymentConfirmedDate: string | null;
  ccNotifyUsers?: Array<{
    id: number;
    firstname: string;
    lastname: string;
    email: string;
  }>;
  invoiceAttachment?: AttachmentType | null;
  attachments: Array<AttachmentType>;
};

export type AttachmentType = {
  archived: boolean;
  createdAt: string;
  displayName: string;
  filename: string;
  hash: string;
  id: number;
  key: string;
  modifiedAt: string;
  url: string;
};

export type InventoryShippingType = {
  carrier: { id: number; name: string };
  priority: string;
  instructions: string;
  shippingDate: string;
  trackingNumber: string;
  status?: string;
};

export type OrganizationModelBundleType = {
  id: number;
  description: string;
  name: string;
  organization: Pick<OrganizationType, 'name' | 'id'>;
  models: Array<ModelType>;
};

export type AccessoryBundleItemType = {
  id: number;
  count: number;
  model: ModelType;
};

export type AccessoryBundleType = {
  id: number;
  name: string;
  description?: string;
  archived: boolean;
  createdAt: string;
  modifiedAt: string;
  items: AccessoryBundleItemType[];
};

export enum FulfillmentOrderLogActionType {
  CREATE = 'create',
  INVOICE_CREATED = 'invoice_created',
  PAYMENT_RECEIVED = 'payment_received',
  ASSIGN_WAREHOUSE = 'assign_warehouse',
  APPROVE = 'approve',
  ASSIGN_DEVICES = 'assign_devices',
  ADD_SHIPPING_DETAILS = 'add_shipping_details',
  CONFIRM_DELIVERY = 'confirm_delivery',
  UPDATE_STATUS = 'update_status',

  CREATE_PACKAGE = 'create_package',
  UPDATE_PACKAGE = 'update_package',
  DELETE_PACKAGE = 'delete_package',
  ASSIGN_DEVICES_TO_PACKAGE = 'assign_devices_to_package',
  ADD_SHIPPING_DETAILS_TO_PACKAGE = 'add_shipping_details_to_package',
}

export enum OrderLogActionType {
  CREATE = 'create',
  UPDATE_STATUS = 'update_status',
  UPDATE_PAYMENT_CHECKER = 'update_payment_checker',
  UPDATE_QUALITY_CHECKER = 'update_quality_checker',
  UPDATE_INVOICE_CHECKER = 'update_invoice_checker',
  INVOICE_CREATED = 'invoice_created',
  ADD_COMMENT = 'add_comment',
  ADD_ATTACHMENT = 'add_attachment',
  ASSIGN_WAREHOUSE = 'assign_warehouse',
  ASSIGN_DEVICES = 'assign_devices',
  CREATE_PACKAGE = 'create_package',
  UPDATE_PACKAGE = 'update_package',
  DELETE_PACKAGE = 'delete_package',
  CONFIRM_DELIVERY = 'confirm_delivery',
  UPDATE_SHIPPING_ADDRESS = 'update_shipping_address',
  CONFIRM_QA = 'confirm_qa',
  FAIL_QA = 'fail_qa',
  RETRY_QA = 'retry_qa',
  ACCEPT_ORDER = 'accept_order',
}

export interface FulfillmentOrderLogType {
  id: number;
  createdAt: string;
  modifiedAt: string;
  previousValue: string | null;
  newValue: string | null;
  userId: number | null;
  userName: string | null;
  action: string;
  fulfillmentOrderId: number;
}

export interface OrderLogType {
  id: number;
  createdAt: string;
  modifiedAt: string;
  previousValue: string | null;
  newValue: string | null;
  userId: number | null;
  userName: string | null;
  action: string;
  orderId: number;
}

export enum DeviceStatus {
  New = 'New',
  InService = 'In service',
  ReturnedPendingTesting = 'Returned (Pending Testing)',
  ReturnedQACheck = 'Returned (QA Check)',
  OutOfService = 'Out of Service',
  Refurbished = 'Refurbished',
}

export type InventoryDeviceDetailsType = DeviceType;

export type NonSerializedItemType = {
  id: number;
  count: number;
  pendingIn: number;
  pendingOut: number;
  available: number;
  model: ModelType;
  warehouse: WarehouseType;
  createdAt: string;
  modifiedAt: string;
};

export type AddNonSerializedItemsToInventoryDto = {
  warehouseId: number;
  modelId: number;
  count: number;
};

export type NonSerializedItemLogType = {
  id: number;
  count: number;
  action: string;
  userId: number | null;
  userName: string | null;
  createdAt: string;
};
